import * as React from 'react'

import { clsx } from 'clsx'

import { Slot } from '@buffer-mono/popcorn'

import styles from './SidePanel.module.css'

/* -------------------------------------------------------------------------------------------------
 * SidePanel (Root)
 * ----------------------------------------------------------------------------------------------- */

/**
 * Animated side panel container rendered as an `<aside>`.
 * Manages open/close animation and provides consistent layout for heading and body sub-components.
 *
 * @example
 * ```tsx
 * <SidePanel open={isOpen}>
 *   <SidePanel.Header>
 *     <SidePanel.Heading>Post Preview</SidePanel.Heading>
 *     <Tooltip content="...">
 *       <InfoIcon />
 *     </Tooltip>
 *   </SidePanel.Header>
 *   <SidePanel.Body>
 *     <ScrollArea>...</ScrollArea>
 *   </SidePanel.Body>
 * </SidePanel>
 * ```
 */
type SidePanelRootProps = React.ComponentPropsWithRef<'aside'> & {
  open: boolean
}

const Root = React.forwardRef<HTMLElement, SidePanelRootProps>(
  ({ open, className, children, ...rest }, ref) => {
    if (!open) {
      return null
    }

    return (
      <aside
        ref={ref}
        className={clsx(styles.sidePanel, className)}
        data-state={open ? 'open' : 'closed'}
        {...rest}
      >
        {children}
      </aside>
    )
  },
)

Root.displayName = 'SidePanel'

/* -------------------------------------------------------------------------------------------------
 * SidePanel.Header
 * ----------------------------------------------------------------------------------------------- */

type SidePanelHeaderProps = React.ComponentPropsWithRef<'div'>

const Header = React.forwardRef<HTMLDivElement, SidePanelHeaderProps>(
  ({ className, children, ...rest }, ref) => {
    return (
      <div ref={ref} className={clsx(styles.header, className)} {...rest}>
        {children}
      </div>
    )
  },
)

Header.displayName = 'SidePanel.Header'

/* -------------------------------------------------------------------------------------------------
 * SidePanel.Heading
 * ----------------------------------------------------------------------------------------------- */

/**
 * Heading typography for the side panel. Renders an `<h2>` by default.
 *
 * Use `asChild` to merge heading styles onto a custom element instead of
 * rendering the default `<h2>` wrapper.
 */
type SidePanelHeadingProps = React.ComponentPropsWithRef<'h2'> & {
  asChild?: boolean
}

const Heading = React.forwardRef<HTMLHeadingElement, SidePanelHeadingProps>(
  ({ asChild, className, children, ...rest }, ref) => {
    const Component = asChild ? Slot.Root : 'h2'

    return (
      <Component
        ref={ref}
        className={clsx(styles.heading, className)}
        {...rest}
      >
        {children}
      </Component>
    )
  },
)

Heading.displayName = 'SidePanel.Heading'

/* -------------------------------------------------------------------------------------------------
 * SidePanel.Body
 * ----------------------------------------------------------------------------------------------- */

/**
 * Flexible body section that fills the remaining space in the side panel.
 * Renders a `<div>` by default.
 *
 * Use `asChild` to merge body styles onto a single element child (e.g. a
 * `<ScrollArea>`), avoiding an extra wrapper DOM node.
 */
type SidePanelBodyProps = React.ComponentPropsWithRef<'div'> & {
  asChild?: boolean
}

const Body = React.forwardRef<HTMLDivElement, SidePanelBodyProps>(
  ({ asChild, className, children, ...rest }, ref) => {
    const Component = asChild ? Slot.Root : 'div'

    return (
      <Component ref={ref} className={clsx(styles.body, className)} {...rest}>
        {children}
      </Component>
    )
  },
)

Body.displayName = 'SidePanel.Body'

/* -------------------------------------------------------------------------------------------------
 * Export
 * ----------------------------------------------------------------------------------------------- */

export const SidePanel = Object.assign(Root, {
  Header,
  Heading,
  Body,
})

export type {
  SidePanelRootProps,
  SidePanelHeadingProps,
  SidePanelHeaderProps,
  SidePanelBodyProps,
}
